import {
  Box,
  Palette,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { setLevel, setThinking } from "@redux/slices/askDostChat";
import { RootState } from "@redux/store";
import { Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";

// Hooks & Types
import { PendingUpload, useFileUpload } from "../hooks/useFileUpload";
import { useSpeechRecognition } from "../hooks/useSpeechRecognition";
import {
  askDostRotatingBorderStyle,
  askDostShadowStyle,
  getAskDostInnerBoxStyle,
} from "../styles/askDostStyles";
import { IAttachment, TAnswerMode } from "../types";
import { MAX_CHAR_LIMIT } from "../utils/constants";
import { AskDostUploadModal } from "./uploads/AskDostUploadModal";
import { FileUpload } from "./uploads/FileUpload";

interface AskDostInputProps {
  value: string;
  onChange: (value: string) => void;
  attachments: IAttachment[];
  onAttachmentsChange: (attachments: IAttachment[]) => void;
  onSubmit: () => void;
  isSending: boolean;
  palette: Palette;
  translate: (text: string, options?: any) => string;
  onCancel?: () => void;
  disabled?: boolean;
  isFullWidth?: boolean;
  showRotatingGlow?: boolean;
  initialPendingUploads?: PendingUpload[];
  onPendingUploadsChange?: (pendingUploads: PendingUpload[]) => void;
}

type TModelOption = "auto" | "recommended" | "minimal" | "strong";

export const AskDostInput = ({
  value,
  onChange,
  attachments,
  onAttachmentsChange,
  onSubmit,
  isSending,
  palette,
  translate,
  onCancel,
  disabled = false,
  isFullWidth = false,
  showRotatingGlow = false,
  initialPendingUploads,
  onPendingUploadsChange,
}: AskDostInputProps) => {
  const textFieldRef = useRef<HTMLInputElement>(null);
  const { breakpoints } = useTheme();
  const fullScreen = useMediaQuery(breakpoints.down("sm"));

  const [isShowModal, setIsShowModal] = useState(false);
  const [isDragActive, setIsDragActive] = useState(false);
  const isOverLimit = value.length > MAX_CHAR_LIMIT;

  const dispatch = useDispatch();
  const chatSettings = useSelector((state: RootState) => state.askDostChat);
  const { setValue } = useFormContext();

  const uiModelOption = chatSettings?.level as TModelOption;

  const currentMode = (
    chatSettings?.thinking ? "think" : "fast"
  ) as TAnswerMode;

  const handleModelChange = (option: string) => {
    const newLevel = option as TModelOption;

    setValue("level", newLevel, { shouldDirty: true, shouldValidate: true });
    dispatch(setLevel(newLevel));
  };

  const handleModeChange = (selectedMode: string) => {
    const isThinkingActive = selectedMode === "think";

    setValue("thinking", isThinkingActive, {
      shouldDirty: true,
      shouldValidate: true,
    });
    dispatch(setThinking(isThinkingActive));
  };

  const handleAddTranscript = (transcript: string) => {
    const separator = value ? " " : "";
    onChange(value + separator + transcript);
  };

  const {
    isListening,
    isPaused,
    liveTranscript,
    toggleListening,
    confirmListening,
    cancelListening,
    pauseListening,
    resumeListening,
  } = useSpeechRecognition({
    onConfirm: handleAddTranscript,
  });

  const handlePauseToggle = () => {
    if (isPaused) {
      resumeListening();
    } else {
      pauseListening();
    }
  };

  const {
    pendingUploads,
    isUploading,
    uploadFiles,
    removePendingUpload,
    resetPendingUploads,
  } = useFileUpload({
    attachments,
    onAttachmentsChange,
    translate,
    initialPendingUploads,
    onPendingUploadsChange,
  });

  const handleSubmit = () => {
    resetPendingUploads();
    onSubmit();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      if (event.shiftKey) {
        return;
      }
      event.preventDefault();
      if (!isSending && value.trim() && !isOverLimit) {
        handleSubmit();
      }
    }
  };

  const canDropFiles = !isSending && !disabled;

  const handleDragOver = (event: React.DragEvent) => {
    if (!canDropFiles) return;
    event.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragActive(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragActive(false);
    if (!canDropFiles) return;
    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      uploadFiles(files);
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    const items = event.clipboardData?.items;
    if (!items) return;

    const files = Array.from(items)
      .filter((item) => item.kind === "file")
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);

    if (files.length > 0) {
      event.preventDefault();
      uploadFiles(files);
    }
  };

  useEffect(() => {
    setValue("focus", chatSettings?.focus || "auto", {
      shouldDirty: true,
      shouldValidate: true,
    });
  }, [chatSettings?.focus, setValue]);

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 800,
          margin: "0 auto",
        }}
      >
        <Box sx={askDostShadowStyle} />
        <Box
          sx={
            showRotatingGlow
              ? askDostRotatingBorderStyle
              : { position: "relative", zIndex: 1, width: "100%" }
          }
        >
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={getAskDostInnerBoxStyle(isDragActive, palette)}
          >
            <FileUpload
              palette={palette}
              translate={translate}
              disabled={isSending || disabled}
              isListening={isListening}
              isSending={isSending}
              toggleListening={toggleListening}
              onConfirmRecording={confirmListening}
              onCancelRecording={cancelListening}
              liveTranscript={liveTranscript}
              isPaused={isPaused}
              onPauseToggle={handlePauseToggle}
              value={value}
              onSubmit={handleSubmit}
              onCancel={onCancel}
              pendingUploads={pendingUploads}
              isUploading={isUploading}
              uploadFiles={uploadFiles}
              removePendingUpload={removePendingUpload}
              onAttachmentClick={() => setIsShowModal(true)}
              answerMode={currentMode}
              onModeChange={handleModeChange}
              selectedModelId={uiModelOption}
              onModelChange={handleModelChange}
            >
              <TextField
                inputRef={textFieldRef}
                placeholder="Ask Dost..."
                fullWidth
                multiline
                maxRows={4}
                minRows={1}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                disabled={disabled}
                sx={{
                  mt: "0px !important",
                  mb: "0px !important",
                  margin: "0px !important",
                  "& .MuiInputBase-root": {
                    marginTop: "0px !important",
                  },
                  "& .MuiInput-root": {
                    width: "100%",
                    fontSize: "15px",
                    marginTop: 0,
                  },
                  "& .MuiInputBase-root.Mui-focused": {
                    outline: "none !important",
                    boxShadow: "none !important",
                  },
                  "& .MuiInputBase-input, & textarea": {
                    outline: "none !important",
                    boxShadow: "none !important",
                  },
                  "& .MuiInputBase-input:focus, & .MuiInputBase-input:focus-visible, & textarea:focus, & textarea:focus-visible":
                    {
                      outline: "none !important",
                      boxShadow: "none !important",
                    },
                  width: "100%",
                }}
              />
            </FileUpload>
          </Box>
        </Box>
      </Box>

      {currentMode === "think" && (
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ mt: 1, justifyContent: "center" }}
        >
          <Sparkles size={12} color={palette.secondary.dark} />
          <Typography variant="caption" sx={{ color: palette.secondary.dark }}>
            {translate("thinking_mode_on_notice")}
          </Typography>
        </Stack>
      )}

      <AskDostUploadModal
        isOpen={isShowModal}
        onClose={() => setIsShowModal(false)}
        onUpload={uploadFiles}
        isUploading={isUploading}
        translate={translate}
        fullScreen={fullScreen}
      />
    </>
  );
};
